import type { HeroId } from '../types';
import { HEROES } from '../heroes';
import { LOADOUT_SAVE_KEY, POWERUPS, autoLoadout, normalizeLoadout, powerupById } from '../powerups';
import { checksumOf } from './canonical';
import { maxLevel } from './economy';
import { emptyCosmetics, migrateShelfDecor, normalizeCosmetics } from './cosmetics';
import { writeStoredProfile } from './peek';
import { emptyStreak, syncEvent } from './streak';
import { pushLedger } from './ledger';
import { adoptRoster } from './adopt';
import { CRITTER_GATES } from './tables';
import { bandForBest, syncCritters } from './trophies';
import { emptyCharms } from '../charms/stake';
import { emptyLog } from './hollowLog';
import { emptyDailySpin, ensureDailySpin } from './dailySpin';
import { ECONOMY_REV, migrateEconomy } from './migrateEconomy';
import { profileKeys } from './storageKey';
import { applyUnlockAll, unlockBuild } from './unlockAll';
import { LEGACY_KEYS, PROFILE_VERSION, type Profile } from './types';

interface Store {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const HERO_IDS = Object.keys(HEROES) as HeroId[];

export type LoadResult = { ok: true; profile: Profile; clockBack: boolean } | { ok: false; newer: true };

function hasLegacy(store: Store): boolean {
  return LEGACY_KEYS.some((key) => store.getItem(key) !== null);
}

/** A legacy browser that already started a duel. Preferences alone do not count. */
function legacyDuel(store: Store): boolean {
  if (store.getItem('critter.coach') === 'seen') return true;
  if (store.getItem(LOADOUT_SAVE_KEY)) return true;
  return false;
}

function inferTutorialSeen(store: Store, profile: Profile): boolean {
  if (profile.settled.length > 0) return true;
  if (profile.ledger.some((row) => row.reason === 'match')) return true;
  return store.getItem('critter.coach') === 'seen';
}

function ensureTutorial(store: Store, profile: Profile): void {
  if (typeof profile.tutorialSeen === 'boolean') return;
  profile.tutorialSeen = inferTutorialSeen(store, profile);
}

/** Tricks an old browser already had in the bag. New road tricks wait for their stop. */
const GRANDFATHER_TRICKS = new Set<string>([
  'quill-burst',
  'dandelion-drift',
  'honey-drip',
  'thicket-shield',
  'pilfer',
  'undermine',
  'moonlit-mark',
  'vanishing-trail',
  'riptide',
  'waterfall',
  'rootsnare',
  'hearthstone',
  'mushroom-ring',
  'sapper-charge',
  'fox-mask',
  'snail-mail',
  'shadow-swap',
  'rainwash',
  'bubble-lift',
  'rolling-thunder',
  'firefly-line',
  'acorn-pop',
]);

/** A gate at 0 is part of the opening cast. Later critters stay locked until their mark. */
function starterUnlocked(id: HeroId): boolean {
  const gate = CRITTER_GATES.find((row) => row.id === id);
  return gate !== undefined && gate.at === 0;
}

function openStarters(profile: Profile): void {
  for (const id of HERO_IDS) {
    if (starterUnlocked(id)) profile.critters[id].unlocked = true;
  }
}

function blank(now: number, grandfather: boolean, loadouts: Profile['loadouts'], tutorialSeen: boolean): Profile {
  const critters = {} as Profile['critters'];
  for (const id of HERO_IDS) critters[id] = { unlocked: starterUnlocked(id) };
  const tricks: Profile['tricks'] = {};
  const trickStock: Profile['trickStock'] = {};
  if (grandfather) {
    for (const power of POWERUPS) {
      if (!GRANDFATHER_TRICKS.has(power.id)) continue;
      tricks[power.id] = { level: 1 };
      trickStock[power.id] = 5;
    }
  } else {
    tricks['quill-burst'] = { level: 1 };
    trickStock['quill-burst'] = 5;
  }
  const profile: Profile = {
    v: 1,
    seq: 0,
    accountId: null,
    wallet: { acorns: 0, honey: 0 },
    trophies: 0,
    bestTrophies: 0,
    critters,
    tricks,
    trickStock,
    charms: emptyCharms(),
    log: emptyLog(),
    dailySpin: emptyDailySpin(),
    economyRev: ECONOMY_REV,
    loadouts,
    road: { claimed: grandfather ? [] : [0] },
    streak: emptyStreak(),
    cosmetics: emptyCosmetics(),
    entitlements: {},
    settled: [],
    tutorialSeen,
    lastSeenAt: now,
    ledger: [],
    checksum: '',
  };
  pushLedger(profile, now, 'migration', {}, 0, grandfather ? 'grandfather' : 'new');
  return profile;
}

function loadoutsFromLegacy(store: Store): Profile['loadouts'] {
  const raw = store.getItem(LOADOUT_SAVE_KEY);
  let bag: Record<string, unknown> = {};
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) bag = parsed as Record<string, unknown>;
    } catch {
      bag = {};
    }
  }
  const loadouts = {} as Profile['loadouts'];
  for (const hero of HERO_IDS) {
    const value = bag[hero];
    if (Array.isArray(value)) {
      loadouts[hero] = normalizeLoadout(
        hero,
        value.filter((item): item is string => typeof item === 'string'),
      );
    } else {
      loadouts[hero] = autoLoadout(hero);
    }
  }
  return loadouts;
}

function freshLoadouts(): Profile['loadouts'] {
  const loadouts = {} as Profile['loadouts'];
  for (const id of HERO_IDS) loadouts[id] = [];
  loadouts.pip = ['quill-burst'];
  return loadouts;
}

function isInt(value: unknown, min = 0): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= min;
}

function isValidProfile(data: Record<string, unknown>): data is Profile & Record<string, unknown> {
  if (data.v !== PROFILE_VERSION || data.accountId !== null) return false;
  if (data.checksum !== checksumOf(data)) return false;
  const wallet = data.wallet as { acorns?: unknown; honey?: unknown } | undefined;
  if (!wallet || !isInt(wallet.acorns) || !isInt(wallet.honey)) return false;
  if (!isInt(data.trophies) || !isInt(data.bestTrophies)) return false;
  if ((data.bestTrophies as number) < (data.trophies as number)) return false;
  if ((data.trophies as number) < bandForBest(data.bestTrophies as number).floor) return false;
  if (!isInt(data.seq) || !isInt(data.lastSeenAt)) return false;

  const critters = data.critters as Record<string, { unlocked?: unknown }> | undefined;
  if (!critters || typeof critters !== 'object') return false;
  const critterKeys = Object.keys(critters);
  if (critterKeys.length !== HERO_IDS.length) return false;
  for (const id of HERO_IDS) {
    if (!critters[id] || typeof critters[id].unlocked !== 'boolean') return false;
  }

  const tricks = data.tricks as Record<string, { level?: unknown }> | undefined;
  if (!tricks || typeof tricks !== 'object' || Array.isArray(tricks)) return false;
  for (const [id, trick] of Object.entries(tricks)) {
    const def = powerupById(id);
    if (!def) return false;
    if (!trick || !isInt(trick.level, 1) || trick.level > maxLevel(def.cost)) return false;
  }

  const loadouts = data.loadouts as Record<string, unknown> | undefined;
  if (!loadouts || typeof loadouts !== 'object') return false;
  for (const id of HERO_IDS) {
    if (!Array.isArray(loadouts[id])) return false;
  }

  const road = data.road as { claimed?: unknown } | undefined;
  if (!road || !Array.isArray(road.claimed) || road.claimed.some((mark) => !isInt(mark))) return false;

  const streak = data.streak as Profile['streak'] | undefined;
  if (!streak || typeof streak !== 'object') return false;
  if (streak.eventId !== null && typeof streak.eventId !== 'string') return false;
  if (streak.endsAt !== null && typeof streak.endsAt !== 'number') return false;
  if (!isInt(streak.wins)) return false;
  if (streak.save !== 'locked' && streak.save !== 'ready' && streak.save !== 'used') return false;
  if (!Array.isArray(streak.claimed) || streak.claimed.some((mark) => !isInt(mark))) return false;
  if (!Array.isArray(streak.closedIds) || streak.closedIds.some((id) => typeof id !== 'string')) return false;

  const cosmetics = data.cosmetics as { ribbon?: unknown } | undefined;
  if (!cosmetics || typeof cosmetics.ribbon !== 'boolean') return false;
  if (!data.entitlements || typeof data.entitlements !== 'object' || Array.isArray(data.entitlements)) return false;
  if (!Array.isArray(data.settled) || data.settled.some((id) => typeof id !== 'string')) return false;
  if ('tutorialSeen' in data && typeof data.tutorialSeen !== 'boolean') return false;
  if (!Array.isArray(data.ledger)) return false;
  for (const entry of data.ledger) {
    if (!entry || typeof entry !== 'object') return false;
    const row = entry as { seq?: unknown; at?: unknown; reason?: unknown; currencies?: unknown; trophies?: unknown };
    if (!isInt(row.seq) || !isInt(row.at) || typeof row.reason !== 'string') return false;
    if (!isInt(row.trophies, -1_000_000)) return false;
    if (!row.currencies || typeof row.currencies !== 'object') return false;
  }
  return true;
}

type Read = { tag: 'empty' } | { tag: 'bad' } | { tag: 'newer' } | { tag: 'ok'; profile: Profile };

function readBlob(raw: string | null): Read {
  if (raw === null || raw === '') return { tag: 'empty' };
  let data: unknown;
  try {
    data = JSON.parse(raw) as unknown;
  } catch {
    return { tag: 'bad' };
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) return { tag: 'bad' };
  const record = data as Record<string, unknown>;
  if (typeof record.v === 'number' && record.v > PROFILE_VERSION) return { tag: 'newer' };
  if (record.checksum !== checksumOf(record)) return { tag: 'bad' };
  adoptRoster(record);
  if (!isValidProfile(record)) return { tag: 'bad' };
  return { tag: 'ok', profile: record as unknown as Profile };
}

function migrate(store: Store, now: number): Profile {
  const grandfather = hasLegacy(store);
  const loadouts = grandfather ? loadoutsFromLegacy(store) : freshLoadouts();
  const profile = blank(now, grandfather, loadouts, legacyDuel(store));
  syncEvent(profile, now);
  saveProfile(store, profile, now);
  return profile;
}

function finish(store: Store, profile: Profile, now: number): LoadResult {
  normalizeCosmetics(profile);
  migrateShelfDecor(profile);
  openStarters(profile);
  syncCritters(profile);
  if (profile.economyRev !== ECONOMY_REV) migrateEconomy(profile, now);
  if (!profile.trickStock) profile.trickStock = {};
  if (!profile.charms) profile.charms = emptyCharms();
  if (!profile.log) profile.log = emptyLog();
  ensureDailySpin(profile);
  if (unlockBuild()) applyUnlockAll(profile);
  const clock = syncEvent(profile, now);
  ensureTutorial(store, profile);
  if (!clock.clockBack) saveProfile(store, profile, now);
  return { ok: true, profile, clockBack: clock.clockBack };
}

/** The first finished or quit duel. Later duels, including rematch, stay quiet. */
export function noteTutorialSeen(store: Store, now: number): void {
  const loaded = loadProfile(store, now);
  if (!loaded.ok || loaded.profile.tutorialSeen) return;
  loaded.profile.tutorialSeen = true;
  saveProfile(store, loaded.profile, now);
}

/** Boot read. A newer version is left untouched. A torn blob falls back to the backup, then a new glen. */
export function loadProfile(store: Store, now: number): LoadResult {
  const keys = profileKeys();
  const raw = store.getItem(keys.profile);
  const primary = readBlob(raw);
  if (primary.tag === 'newer') return { ok: false, newer: true };
  if (primary.tag === 'ok') return finish(store, primary.profile, now);

  if (primary.tag === 'bad' && raw) store.setItem(keys.quarantine, raw);
  const backup = readBlob(store.getItem(keys.backup));
  if (backup.tag === 'newer') return { ok: false, newer: true };
  if (backup.tag === 'ok') {
    writeStoredProfile(store, backup.profile);
    return finish(store, backup.profile, now);
  }
  return { ok: true, profile: migrate(store, now), clockBack: false };
}

/** Stamp the clock when it has not stepped backward, then write the profile and the legacy loadout key. */
export function saveProfile(store: Store, profile: Profile, now: number): void {
  if (now >= profile.lastSeenAt) profile.lastSeenAt = now;
  writeStoredProfile(store, profile);
  store.setItem(LOADOUT_SAVE_KEY, JSON.stringify(profile.loadouts));
}
